import React, { useCallback, useEffect, useRef } from 'react';
import { Drawer, Grid, Modal, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';

import type { PosItem, PosItemChild } from '../api/usePosItemsSearch';
import { getFormattedPrice } from '@tools/utils/common.tsx';

import './ItemVariantSelectionModal.scss';

interface ItemVariantSelectionModalProps {
  open: boolean;
  item: PosItem | null;
  onClose: () => void;
  onSelectChild: (parentItem: PosItem, child: PosItemChild) => void;
}

export const ItemVariantSelectionModal: React.FC<ItemVariantSelectionModalProps> = ({
  open,
  item,
  onClose,
  onSelectChild,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const firstChildRef = useRef<HTMLButtonElement | null>(null);
  const children = item?.children ?? [];

  useEffect(() => {
    if (!open || children.length === 0) {
      return;
    }
    const focusTimer = window.setTimeout(() => firstChildRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [children.length, open, item?.id]);

  const handleChildClick = useCallback(
    (child: PosItemChild) => {
      if (!item) {
        return;
      }
      onSelectChild(item, child);
    },
    [item, onSelectChild],
  );

  if (!item) {
    return null;
  }

  const title = (
    <div className="pos-variant-picker__title">
      <Typography.Text type="secondary">Variantes</Typography.Text>
      <Typography.Title level={4}>{item.name}</Typography.Title>
    </div>
  );

  const content = (
    <div className="pos-variant-picker__list" role="listbox" aria-label={`Variantes de ${item.name}`}>
      {children.map((child, index) => {
        const price = child.price?.value;
        const currency = child.price?.currency ?? item.price?.currency ?? 'ARS';
        return (
          <button
            key={child.id}
            ref={index === 0 ? firstChildRef : undefined}
            type="button"
            role="option"
            aria-label={`Agregar ${child.name}`}
            className="pos-variant-picker__option"
            onClick={() => handleChildClick(child)}
          >
            <span className="pos-variant-picker__option-copy">
              <strong>{child.name}</strong>
              {child.sku ? <small>SKU: {child.sku}</small> : null}
            </span>
            <span className="pos-variant-picker__option-action">
              {price != null ? getFormattedPrice(price, currency) : 'Sin precio'}
              <RightOutlined aria-hidden />
            </span>
          </button>
        );
      })}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        placement="bottom"
        height="auto"
        title={title}
        onClose={onClose}
        destroyOnHidden
        className="pos-variant-picker pos-variant-picker--mobile"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      title={title}
      footer={null}
      onCancel={onClose}
      destroyOnHidden
      width={480}
      className="pos-variant-picker"
    >
      {content}
    </Modal>
  );
};

export default ItemVariantSelectionModal;
